import type { CanvasElement, ProjectFile } from '@archer/shared';
import type Konva from 'konva';
import { getShapeLayers } from '../canvas/shapes/definitions';

const escapeXml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Darken a hex color by `amount` (0-1). Falls back to the original color for non-hex values.
const darkenColor = (hex: string, amount: number): string => {
  const m = /^#([0-9a-f]{3,6})$/i.exec(hex);
  if (!m) return hex;
  const [, color] = m;
  if (!color) return hex;
  let c = color;
  if (c.length === 3) {
    c = c
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const r = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(0, 2), 16) * (1 - amount))));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(2, 4), 16) * (1 - amount))));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(4, 6), 16) * (1 - amount))));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Build a self-contained SVG from canvas elements.
 * Native SVG primitives are used so the export is editable in vector tools.
 */
export const exportSvg = (elements: CanvasElement[]): string => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of elements) {
    minX = Math.min(minX, el.x);
    minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + el.width);
    maxY = Math.max(maxY, el.y + el.height);
  }
  if (!isFinite(minX)) return '<svg xmlns="http://www.w3.org/2000/svg"/>';
  const padding = 20;
  const w = maxX - minX + padding * 2;
  const h = maxY - minY + padding * 2;

  const pieces: string[] = [];
  pieces.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="${minX - padding} ${minY - padding} ${w} ${h}">`,
  );
  pieces.push('<defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#1f2937"/></marker></defs>');

  for (const el of elements) {
    const fill = el.fill ?? '#ffffff';
    const stroke = el.stroke ?? '#1f2937';
    const sw = el.strokeWidth ?? 1.5;
    const x = el.x;
    const y = el.y;
    const wEl = el.width;
    const hEl = el.height;
    const tx = el.x + wEl / 2;
    const ty = el.y + hEl / 2;
    let shape = '';
    switch (el.type) {
      case 'arrow':
      case 'line': {
        const pts: number[] = (el as { points?: number[] }).points ?? [];
        if (pts.length < 4) continue;
        const d = pts.map((p: number, i: number) => (i % 2 === 0 ? `${p}` : `,${p}`)).join(' ');
        shape = `<polyline points="${d}" fill="none" stroke="${stroke}" stroke-width="${sw}" marker-end="${el.type === 'arrow' ? 'url(#arrow)' : ''}"/>`;
        break;
      }
      default:
        // All other shape types share their geometry with the canvas renderer via
        // `getShapeLayers` - see shapes/definitions.ts for the single source of truth.
        for (const layer of getShapeLayers(el.type, wEl, hEl)) {
          const layerFill = layer.fill === 'none' ? 'none' : layer.fill === 'dark' ? darkenColor(fill, 0.1) : fill;
          const layerStroke = layer.stroke ? stroke : 'none';
          shape += `<path transform="translate(${x} ${y})" d="${layer.d}" fill="${layerFill}" stroke="${layerStroke}" stroke-width="${sw}"/>`;
        }
    }
    pieces.push(`<g transform="rotate(${el.rotation ?? 0} ${tx} ${ty})">${shape}`);
    if (el.text) {
      pieces.push(
        `<text x="${tx}" y="${ty}" font-family="Inter, sans-serif" font-size="${el.fontSize ?? 14}" fill="${el.textColor ?? '#0f172a'}" text-anchor="middle" dominant-baseline="middle">${escapeXml(el.text)}</text>`,
      );
    }
    pieces.push('</g>');
  }
  pieces.push('</svg>');
  return pieces.join('\n');
};

export const exportPngFromStage = async (stage: Konva.Stage, name: string): Promise<void> => {
  const dataUrl = stage.toDataURL({ pixelRatio: 2 });
  triggerDownload(dataUrl, `${slug(name)}.png`);
};

export const exportJson = (project: ProjectFile): void => {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
  triggerDownload(URL.createObjectURL(blob), `${slug(project.name)}.archer.json`);
};

export const exportProject = async (project: ProjectFile): Promise<void> => {
  const choice = window.prompt('Export as: 1) PNG  2) SVG  3) JSON', '2');
  if (choice === '1' || choice === '2') {
    const svg = exportSvg(project.elements);
    if (choice === '2') {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      triggerDownload(URL.createObjectURL(blob), `${slug(project.name)}.svg`);
    }
    // For PNG we hand off to a hidden Konva stage render — but since the export utility
    // doesn't have direct access to the live stage here, we render the SVG into a Canvas
    // and rasterize it. This works without depending on the editor's live stage.
    const img = new Image();
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 1600;
      canvas.height = img.height || 1200;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) triggerDownload(URL.createObjectURL(blob), `${slug(project.name)}.png`);
        URL.revokeObjectURL(url);
      });
    };
    img.src = url;
  } else if (choice === '3') {
    exportJson(project);
  }
};

const triggerDownload = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const slug = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'archer-export';
